import TreeContainer from './Base';
import TreeIterator from './Base/TreeIterator';
import $checkWithinAccessParams from "../../utils/checkParams.macro";
import { throwIteratorAccessError } from "../../utils/throwError";
class OrderedSetIterator extends TreeIterator {
    constructor(node, header, container, iteratorType) {
        super(node, header, iteratorType);
        this.container = container;
    }
    get pointer() {
        if (this._node === this._header) {
            throwIteratorAccessError();
        }
        return this._node._key;
    }
    copy() {
        return new OrderedSetIterator(this._node, this._header, this.container, this.iteratorType);
    }
}
class OrderedSet extends TreeContainer {
    /**
     * @param container - The initialization container.
     * @param cmp - The compare function.
     * @param enableIndex - Whether to enable iterator indexing function.
     * @example
     * new OrderedSet();
     * new OrderedSet([0, 1, 2]);
     * new OrderedSet([0, 1, 2], (x, y) => x - y);
     * new OrderedSet([0, 1, 2], (x, y) => x - y, true);
     */
    constructor(container = [], cmp, enableIndex) {
        super(cmp, enableIndex);
        const self = this;
        container.forEach(function (el) {
            self.insert(el);
        });
    }
    begin() {
        return new OrderedSetIterator(this._header._left || this._header, this._header, this);
    }
    end() {
        return new OrderedSetIterator(this._header, this._header, this);
    }
    rBegin() {
        return new OrderedSetIterator(this._header._right || this._header, this._header, this, 1 /* IteratorType.REVERSE */);
    }
    rEnd() {
        return new OrderedSetIterator(this._header, this._header, this, 1 /* IteratorType.REVERSE */);
    }
    front() {
        return this._header._left ? this._header._left._key : undefined;
    }
    back() {
        return this._header._right ? this._header._right._key : undefined;
    }
    lowerBound(key) {
        const resNode = this._lowerBound(this._root, key);
        return new OrderedSetIterator(resNode, this._header, this);
    }
    upperBound(key) {
        const resNode = this._upperBound(this._root, key);
        return new OrderedSetIterator(resNode, this._header, this);
    }
    reverseLowerBound(key) {
        const resNode = this._reverseLowerBound(this._root, key);
        return new OrderedSetIterator(resNode, this._header, this);
    }
    reverseUpperBound(key) {
        const resNode = this._reverseUpperBound(this._root, key);
        return new OrderedSetIterator(resNode, this._header, this);
    }
    forEach(callback) {
        this._inOrderTraversal(function (node, index, set) {
            callback(node._key, index, set);
        });
    }
    /**
     * @description Insert element to set.
     * @param key - The key want to insert.
     * @param hint - You can give an iterator hint to improve insertion efficiency.
     * @return The size of container after setting.
     * @example
     * const st = new OrderedSet([2, 4, 5]);
     * const iter = st.begin();
     * st.insert(1);
     * st.insert(3, iter);  // give a hint will be faster.
     */
    insert(key, hint) {
        return this._set(key, undefined, hint);
    }
    getElementByPos(pos) {
        if (pos < 0 || pos > this._length - 1) {
            throw new RangeError();
        }
        const node = this._inOrderTraversal(pos);
        return node._key;
    }
    find(element) {
        const resNode = this._getTreeNodeByKey(this._root, element);
        return new OrderedSetIterator(resNode, this._header, this);
    }
    union(other) {
        const self = this;
        other.forEach(function (el) {
            self.insert(el);
        });
        return this._length;
    }
    *[Symbol.iterator]() {
        const length = this._length;
        const nodeList = this._inOrderTraversal();
        for (let i = 0; i < length; ++i) {
            yield nodeList[i]._key;
        }
    }
}
export default OrderedSet;
